/**
 * Custom hook for keyboard shortcuts
 */
import { useEffect } from 'react';
import { useAudio } from '../contexts/AudioContext';

const useKeyboardShortcuts = () => {
  const {
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isShuffle,
    isRepeat,
    togglePlay,
    skipToNext,
    skipToPrevious,
    seekTo,
    changeVolume,
    toggleMute,
    toggleShuffle,
    toggleRepeat,
  } = useAudio();

  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ignore if user is typing in an input field
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable
      ) {
        return;
      }

      // Prevent default for handled keys
      let handled = false;

      switch (event.key) {
        case ' ': // Space - Play/Pause
          event.preventDefault();
          if (currentSong) {
            togglePlay();
          }
          handled = true;
          break;

        case 'ArrowRight': // Right Arrow - Forward 10 seconds
          event.preventDefault();
          if (currentSong && duration) {
            const newTime = Math.min(currentTime + 10, duration);
            seekTo(newTime);
          }
          handled = true;
          break;

        case 'ArrowLeft': // Left Arrow - Backward 10 seconds
          event.preventDefault();
          if (currentSong) {
            const newTime = Math.max(currentTime - 10, 0);
            seekTo(newTime);
          }
          handled = true;
          break;

        case 'ArrowUp': // Up Arrow - Volume Up
          event.preventDefault();
          const newVolumeUp = Math.min(volume + 0.1, 1);
          changeVolume(newVolumeUp);
          handled = true;
          break;

        case 'ArrowDown': // Down Arrow - Volume Down
          event.preventDefault();
          const newVolumeDown = Math.max(volume - 0.1, 0);
          changeVolume(newVolumeDown);
          handled = true;
          break;

        case 'm': // M - Mute/Unmute
        case 'M':
          event.preventDefault();
          toggleMute();
          handled = true;
          break;

        case 's': // S - Toggle Shuffle
        case 'S':
          event.preventDefault();
          toggleShuffle();
          handled = true;
          break;

        case 'r': // R - Toggle Repeat
        case 'R':
          event.preventDefault();
          toggleRepeat();
          handled = true;
          break;

        case 'n': // N - Next Track
        case 'N':
          event.preventDefault();
          skipToNext();
          handled = true;
          break;

        case 'p': // P - Previous Track
        case 'P':
          event.preventDefault();
          skipToPrevious();
          handled = true;
          break;

        case '?': // ? - Show Help
          event.preventDefault();
          showKeyboardHelp();
          handled = true;
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    togglePlay,
    skipToNext,
    skipToPrevious,
    seekTo,
    changeVolume,
    toggleMute,
    toggleShuffle,
    toggleRepeat,
  ]);
};

// Show keyboard shortcuts help modal
const showKeyboardHelp = () => {
  const shortcuts = `
Keyboard Shortcuts Help:

Playback Controls:
  Space        - Play/Pause
  Arrow Right  - Forward 10 seconds
  Arrow Left   - Backward 10 seconds
  N            - Next Track
  P            - Previous Track

Volume Controls:
  Arrow Up     - Volume Up
  Arrow Down   - Volume Down
  M            - Mute/Unmute

Player Options:
  S            - Toggle Shuffle
  R            - Toggle Repeat

Help:
  ?            - Show this help message

Press ESC to close this window
  `;

  // Create a simple modal overlay
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
  `;

  const content = document.createElement('div');
  content.style.cssText = `
    background: #1e1e2e;
    padding: 30px;
    border-radius: 10px;
    max-width: 500px;
    color: white;
    font-family: monospace;
    white-space: pre-line;
    line-height: 1.6;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  `;

  content.textContent = shortcuts;
  modal.appendChild(content);
  document.body.appendChild(modal);

  const closeModal = () => {
    document.body.removeChild(modal);
    document.removeEventListener('keydown', handleEscape);
    document.removeEventListener('click', closeOnClick);
  };

  const handleEscape = (event) => {
    if (event.key === 'Escape') {
      closeModal();
    }
  };

  const closeOnClick = (event) => {
    if (event.target === modal) {
      closeModal();
    }
  };

  document.addEventListener('keydown', handleEscape);
  document.addEventListener('click', closeOnClick);

  // Auto-close after 10 seconds
  setTimeout(() => {
    if (document.body.contains(modal)) {
      closeModal();
    }
  }, 10000);
};

export default useKeyboardShortcuts;